import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Alert, Box, Grid, TextField, Typography } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import { Link } from 'react-router-dom'
import Api from '../Api'
import LoadingDefault from './Elements/Loading/LoadingDefault'
import { KeDoContext } from '../kedo-context'
import image1 from '../images/image1.webp'
import image2 from '../images/image2.webp'
import image3 from '../images/image3.webp'
import image4 from '../images/image4.webp'
import image5 from '../images/image5.webp'
import { StyledButton } from './Elements/StyledComponents/Buttons'

const ForgotPassword: React.FC = () => {
    const kedo = useContext(KeDoContext)

    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [errors, setErrors] = useState(false)
    const [bgStyle, setBgStyle] = useState<SxProps<Theme>>({})

    useEffect(() => {
        const pictureArray = [image1, image2, image3, image4, image5]
        const randomIndex = Math.floor(Math.random() * pictureArray.length)
        const selectedPicture = pictureArray[randomIndex]

        setBgStyle({ backgroundImage: `url(${selectedPicture})` })
    }, [])

    const onLoginEnterKey = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                (event.target as HTMLInputElement).form?.dispatchEvent(
                    new Event('submit', { cancelable: true, bubbles: true })
                )
            }
        },
        []
    )

    const onSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const api = new Api()
        const form = event.currentTarget

        const formData = {
            username: (form.elements.namedItem('username') as HTMLInputElement)
                .value,
            email: (form.elements.namedItem('email') as HTMLInputElement).value,
        }

        setLoading(true)
        setErrors(false)

        api.postRaw(api.getUserEndpoint() + '/forgot_password', formData)
            .then(() => {
                setLoading(false)
                setSuccess(true)
            })
            .catch(() => {
                setLoading(false)
                setSuccess(false)
                setErrors(true)
            })
    }, [])

    if (success) {
        return (
            <Box
                sx={bgStyle || undefined}
                className="login outerContainer fullHeight"
            >
                <Grid container justifyContent="center">
                    <Grid
                        size={{ xs: 10, md: 8, lg: 4 }}
                        className="mainContainer"
                    >
                        <Typography variant="h1">
                            {kedo.t('Check your mailbox')}
                        </Typography>

                        <Typography sx={{ mb: 2, mt: 1 }}>
                            {kedo.t('forgot_password_check')}
                        </Typography>

                        <Box
                            component={Link}
                            to="/login"
                            sx={{
                                mt: 1,
                                display: 'inline-block',
                                color: 'inherit',
                            }}
                        >
                            <StyledButton>
                                {kedo.t('Back to')}{' '}
                                {kedo.t('Login').toLowerCase()}
                            </StyledButton>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        )
    }

    return (
        <Box
            sx={bgStyle || undefined}
            className="login outerContainer fullHeight"
        >
            <Grid container justifyContent="center">
                <Grid size={{ xs: 10, md: 8, lg: 4 }} className="mainContainer">
                    <Typography variant="h1">
                        {kedo.t('Forgot password')}
                    </Typography>

                    <Typography sx={{ mb: 2, mt: 1 }}>
                        {kedo.t('forgot_password_text')}
                    </Typography>

                    <Box component="form" onSubmit={onSubmit}>
                        {errors && (
                            <Alert severity="error">
                                {kedo.t('Incorrect username or email address')}
                            </Alert>
                        )}

                        <TextField
                            size="small"
                            name="username"
                            autoFocus
                            fullWidth
                            margin="normal"
                            placeholder={kedo.t('Username')}
                            onKeyDown={onLoginEnterKey}
                            slotProps={{
                                input: {
                                    readOnly: loading,
                                },
                            }}
                            required
                        />

                        <TextField
                            size="small"
                            name="email"
                            type="email"
                            fullWidth
                            margin="normal"
                            placeholder={kedo.t('E-mail')}
                            onKeyDown={onLoginEnterKey}
                            slotProps={{
                                input: {
                                    readOnly: loading,
                                },
                            }}
                            required
                        />

                        <StyledButton fullWidth type="submit" sx={{ mt: 2 }}>
                            {loading && <LoadingDefault size="sm" />}
                            &nbsp; {kedo.t('Request a new password')}
                        </StyledButton>

                        <Box
                            component={Link}
                            to="/login"
                            sx={{
                                mt: 1.5,
                                display: 'inline-block',
                                color: 'inherit',
                            }}
                        >
                            <Typography variant="body2" component="small">
                                {kedo.t('Back to')}{' '}
                                {kedo.t('Login').toLowerCase()}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default ForgotPassword
